import React, { useEffect } from "react";
import { Grid, Button, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import AppForm from "components/Shared/AppForm";
import { useShipmentDetailContext } from "contexts/ShipmentDetailContext";
import { useUpdateShipmentMutation } from "store/services/shipmentApi";

const INITIAL_STATE = {
  name: "",
  itemReference: "",
  weight: "",
  dimensions: {
    length: "",
    width: "",
    height: "",
  },
};

const ShipmentItemsForm = ({
  isBusy = false,
  item = null,
  shipmentId,
  onSubmit = () => {},
  onCancel = () => {},
}) => {
  const { ShipmentDetail } = useShipmentDetailContext();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      shipmentId: ShipmentDetail?._id,
    },
  });

  const [updateShipment, { isLoading: updatingShipment }] =
    useUpdateShipmentMutation();

  // Reset form when editing an item
  useEffect(() => {
    if (!item) {
      reset({ ...INITIAL_STATE, shipmentId: ShipmentDetail?._id });
      return;
    }
    reset({
      itemReference: item.itemReference || "",
      name: item.name || "",
      weight: item.weight || "",
      dimensions: {
        length: item.dimensions?.length || "",
        width: item.dimensions?.width || "",
        height: item.dimensions?.height || "",
      },
      shipmentId: ShipmentDetail?._id,
    });
  }, [item, reset, ShipmentDetail?._id]);

  const handleOnSubmit = async (data) => {
    try {
      if (!shipmentId) {
        toast.error("Shipment ID is missing");
        return;
      }

      // Prepare the new or updated item
      const newItem = {
        name: data.name,
        itemReference: data.itemReference,
        weight: Number(data.weight),
        dimensions: {
          length: Number(data.dimensions.length) || 0,
          width: Number(data.dimensions.width) || 0,
          height: Number(data.dimensions.height) || 0,
        },
      };

      // Get current shipmentDetails
      const currentDetails = Array.isArray(ShipmentDetail?.shipmentDetails)
        ? [...ShipmentDetail.shipmentDetails]
        : [];

      // Update or add item
      let updatedDetails;
      if (item && item.index !== undefined) {
        // Editing existing item
        updatedDetails = currentDetails.map((detail, i) =>
          i === item.index ? newItem : detail
        );
      } else {
        // Adding new item
        updatedDetails = [...currentDetails, newItem];
      }

      // Debugging: Log mutation parameters
      console.log("Updating shipment with:", {
        _id: shipmentId,
        shipmentDetails: updatedDetails,
      });

      // Update shipment with new shipmentDetails
      await updateShipment({
        _id: shipmentId, // Changed from id to _id
        shipmentDetails: updatedDetails,
      }).unwrap();

      toast.success(
        item ? "Item updated successfully!" : "Item added successfully!"
      );
      onSubmit(updatedDetails); // Pass updated details to parent
    } catch (err) {
      console.error("Update shipment error:", err);
      toast.error(
        err?.data?.message || `Failed to ${item ? "update" : "add"} item!`
      );
    }
  };

  return (
    <AppForm loading={isBusy || updatingShipment}>
      <AppForm.Content>
        <Grid container spacing={4}>
          <Grid item md={12}>
            <Controller
              name="name"
              control={control}
              rules={{ required: "Item Name is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Item Name"
                  required
                  error={!!errors.name}
                  helperText={errors?.name?.message}
                />
              )}
            />
          </Grid>
          <Grid item md={12}>
            <Controller
              name="itemReference"
              control={control}
              rules={{ required: "Reference is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Reference"
                  required
                  error={!!errors.itemReference}
                  helperText={errors?.itemReference?.message}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="weight"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Weight (kg)"
                  type="number"
                  inputProps={{ min: 0.1, step: 0.1 }}
                  error={!!errors.weight}
                  helperText={errors?.weight?.message}
                  onChange={(e) => {
                    const value =
                      e.target.value === "" ? "" : Number(e.target.value);
                    if (value && value < 0.1) {
                      toast.error("Weight cannot be less than 0.1 Kg");
                    }
                    field.onChange(value);
                  }}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="dimensions.length"
              control={control}
              rules={{
                min: {
                  value: 0.1,
                  message: "Length cannot be less than 0.1",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Length (meter)"
                  type="number"
                  inputProps={{ min: 0.1, step: 0.1 }}
                  error={!!errors.dimensions?.length}
                  helperText={errors?.dimensions?.length?.message}
                  onChange={(e) => {
                    const value =
                      e.target.value === "" ? "" : Number(e.target.value);
                    if (value && value < 0.1) {
                      toast.error("Length cannot be less than 0.1");
                    }
                    field.onChange(value);
                  }}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="dimensions.width"
              control={control}
              rules={{
                min: {
                  value: 0.1,
                  message: "Width cannot be less than 0.1",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Width (meter)"
                  type="number"
                  inputProps={{ min: 0.1, step: 0.1 }}
                  error={!!errors.dimensions?.width}
                  helperText={errors?.dimensions?.width?.message}
                  onChange={(e) => {
                    const value =
                      e.target.value === "" ? "" : Number(e.target.value);
                    if (value && value < 0.1) {
                      toast.error("Width cannot be less than 0.1");
                    }
                    field.onChange(value);
                  }}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="dimensions.height"
              control={control}
              rules={{
                min: {
                  value: 0.1,
                  message: "Height cannot be less than 0.1",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Height (meter)"
                  type="number"
                  inputProps={{ min: 0.1, step: 0.1 }}
                  error={!!errors.dimensions?.height}
                  helperText={errors?.dimensions?.height?.message}
                  onChange={(e) => {
                    const value =
                      e.target.value === "" ? "" : Number(e.target.value);
                    if (value && value < 0.1) {
                      toast.error("Height cannot be less than 0.1");
                    }
                    field.onChange(value);
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button disabled={isBusy || updatingShipment} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isBusy || updatingShipment}
          onClick={handleSubmit(handleOnSubmit)}
        >
          {item ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default ShipmentItemsForm;
